import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Building2, Search, Plus, Globe, MapPin, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CRMPage() {
  const { getAuthHeaders } = useAuth();
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: '', type: 'investor', description: '', headquarters: '', founded: 2020, sectors_focus: [], geographic_focus: [] });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [usersRes, orgsRes] = await Promise.all([
        axios.get(`${API}/users`, { headers: getAuthHeaders() }),
        axios.get(`${API}/organizations`, { headers: getAuthHeaders() })
      ]);
      setUsers(usersRes.data);
      setOrganizations(orgsRes.data);
    } catch (error) {
      console.error('Failed to fetch CRM data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addOrganization = async () => {
    if (!newOrg.name || !newOrg.description || !newOrg.headquarters) { toast.error('Fill required fields'); return; }
    try {
      await axios.post(`${API}/organizations`, newOrg, { headers: getAuthHeaders() });
      toast.success('Organization added');
      setOrgDialogOpen(false);
      setNewOrg({ name: '', type: 'investor', description: '', headquarters: '', founded: 2020, sectors_focus: [], geographic_focus: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add organization');
    }
  };

  const getRoleBadge = (role) => {
    const map = {
      admin: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
      sponsor: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
      investor: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
      bank: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
      epc: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
      buyer: 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300',
      internal: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
    };
    return map[role] || map.internal;
  };

  const filteredUsers = users.filter(u =>
    u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.organization?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredOrgs = organizations.filter(o =>
    o.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.headquarters?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-full"><div className="text-muted-foreground">Loading CRM...</div></div>;

  return (
    <div className="p-6 space-y-6" data-testid="crm-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">CRM</h1>
          <p className="text-muted-foreground">Manage contacts, organizations, and relationships</p>
        </div>
        <Dialog open={orgDialogOpen} onOpenChange={setOrgDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-org-btn"><Plus size={16} className="mr-2" />Add Organization</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Add Organization</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Name *</Label><Input value={newOrg.name} onChange={(e) => setNewOrg({...newOrg, name: e.target.value})} placeholder="Organization name" /></div>
              <div><Label>Type *</Label>
                <Select value={newOrg.type} onValueChange={(v) => setNewOrg({...newOrg, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="investor">Investor</SelectItem>
                    <SelectItem value="sponsor">Sponsor</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="epc">EPC Contractor</SelectItem>
                    <SelectItem value="buyer">Buyer</SelectItem>
                    <SelectItem value="internal">Internal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Headquarters *</Label><Input value={newOrg.headquarters} onChange={(e) => setNewOrg({...newOrg, headquarters: e.target.value})} placeholder="City, Country" /></div>
              <div><Label>Founded</Label><Input type="number" value={newOrg.founded} onChange={(e) => setNewOrg({...newOrg, founded: parseInt(e.target.value)})} /></div>
              <div><Label>Description *</Label><Textarea value={newOrg.description} onChange={(e) => setNewOrg({...newOrg, description: e.target.value})} rows={3} /></div>
              <Button onClick={addOrganization} className="w-full" data-testid="submit-org-btn">Add Organization</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Total Contacts</p><p className="text-2xl font-bold font-mono">{users.length}</p></div><Users className="h-8 w-8 text-muted-foreground" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Organizations</p><p className="text-2xl font-bold font-mono">{organizations.length}</p></div><Building2 className="h-8 w-8 text-muted-foreground" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Investors</p><p className="text-2xl font-bold font-mono">{users.filter(u => u.role === 'investor').length}</p></div><Globe className="h-8 w-8 text-muted-foreground" /></div></CardContent></Card>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
        <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search contacts or organizations..." className="pl-10" data-testid="crm-search" />
      </div>

      <Tabs defaultValue="contacts">
        <TabsList>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts ({filteredUsers.length})</TabsTrigger>
          <TabsTrigger value="organizations" data-testid="tab-organizations">Organizations ({filteredOrgs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="contacts" className="space-y-2 mt-4">
          {filteredUsers.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">No contacts found</CardContent></Card>
          ) : (
            filteredUsers.map(user => (
              <Card key={user.id} data-testid={`contact-${user.id}`}>
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <span className="text-sm font-semibold">{user.full_name?.split(' ').map(n => n[0]).join('')}</span>
                      </div>
                      <div>
                        <p className="font-medium text-sm">{user.full_name}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Mail size={10} />{user.email}</span>
                          {user.organization && <span className="flex items-center gap-1"><Building2 size={10} />{user.organization}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {user.country && <span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={10} />{user.country}</span>}
                      <Badge className={getRoleBadge(user.role)}>{user.role}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="organizations" className="space-y-2 mt-4">
          {filteredOrgs.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">No organizations found. Add your first organization.</CardContent></Card>
          ) : (
            filteredOrgs.map(org => (
              <Card key={org.id} data-testid={`org-${org.id}`}>
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded bg-accent/10 flex items-center justify-center"><Building2 size={18} className="text-accent" /></div>
                      <div>
                        <p className="font-medium text-sm">{org.name}</p>
                        <p className="text-xs text-muted-foreground">{org.description?.substring(0, 80)}...</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={10} />{org.headquarters}</span>
                      <Badge className={getRoleBadge(org.type)}>{org.type}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
